import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';

interface IWizardContext {
	handleExit: (() => void) | null;
	isDisabled: boolean;
	shouldShowExitButton: boolean;
	setWizardState: (state: Partial<IWizardState>) => void;
}

interface IWizardState {
	handleExit: (() => void) | null;
	isDisabled: boolean;
	shouldShowExitButton: boolean;
}

const WizardContext = createContext<IWizardContext | undefined>(undefined);

export const useWizardContext = () => {
	const context = useContext(WizardContext);
	return context; // Return undefined if not in Wizard context (optional usage)
};

interface IWizardProviderProps {
	children: ReactNode;
}

export const WizardProvider: React.FC<IWizardProviderProps> = ({ children }) => {
	const [wizardState, setWizardStateInternal] = useState<IWizardState>({
		handleExit: null,
		isDisabled: false,
		shouldShowExitButton: false,
	});

	const setWizardState = useCallback((state: Partial<IWizardState>) => {
		setWizardStateInternal((prev) => {
			// Only update if values actually changed
			const hasChanged =
				('handleExit' in state && state.handleExit !== prev.handleExit) ||
				('isDisabled' in state && state.isDisabled !== prev.isDisabled) ||
				('shouldShowExitButton' in state && state.shouldShowExitButton !== prev.shouldShowExitButton);

			if (!hasChanged) {
				return prev; // Return same object to prevent re-render
			}

			return { ...prev, ...state };
		});
	}, []);

	const value: IWizardContext = React.useMemo(
		() => ({
			handleExit: wizardState.handleExit,
			isDisabled: wizardState.isDisabled,
			shouldShowExitButton: wizardState.shouldShowExitButton,
			setWizardState,
		}),
		[wizardState.handleExit, wizardState.isDisabled, wizardState.shouldShowExitButton, setWizardState],
	);

	return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

